// Pure helper for the Segment-title radial x-offset (WEB-036 fix,
// audits/FINDINGS-REGISTER.md).
//
// Kept in its own dependency-free module (no `d3` import) rather than inline
// in createPie.js so it stays independently unit-testable: createPie.js
// imports the whole `d3` package, which is ESM-only and isn't transformed by
// this repo's default Jest config, so any test that imports createPie.js
// directly fails to parse. A test importing only this module sidesteps that
// entirely.
export function computeLabelRadialX(outerRadius, midangle) {
  return outerRadius * 0.99 * (midangle < Math.PI ? 1 : -1);
}

// hw-0914-dev-label-pill-clipped-offscreen-1280: a rim-pill's horizontal
// anchor (`pillTx`, derived from `self.pieOutterRadius`) is computed
// independently of the Builder pane's OWN current width. Selecting a segment
// can shrink the pane (BUILDER_SEGMENT_THIRDS) in the same tick that
// createPie's selection-only fast path (syncLabelSelectionTransforms)
// recomputes every OTHER segment's anchor — if `pieOutterRadius` hasn't
// caught up to the pane's new, smaller width yet, the anchor itself (not
// just the label text, which COMPB-02 already clamps) can land outside the
// pane, pushing the whole pill off the visible canvas. MEASURED live
// (web-dev.iyuga.net, 1280x720, fresh 6-segment wheel): a non-selected
// segment's pill settled at screen left:-16/-18 and stayed there
// indefinitely (not a transient race) after a sibling segment was selected.
// Mirrors COMPB-02's existing `paneWidth/2` boundary (segmentContrastGuard.js
// clampCharsToAvailableWidth) so both the anchor's POSITION and its label's
// WIDTH are bounded to the same pane, instead of only the width.
export function clampAnchorTxToPane(tx, paneWidth) {
  if (
    typeof paneWidth !== "number" ||
    !Number.isFinite(paneWidth) ||
    paneWidth <= 0 ||
    typeof tx !== "number" ||
    !Number.isFinite(tx)
  ) {
    return tx;
  }
  const maxTx = paneWidth / 2;
  return Math.max(-maxTx, Math.min(maxTx, tx));
}

// stg-0921-P1-view-wheel-rim-pills-hide-behind-the-open-segment-panel:
// MEASURED live on web-stg.iyuga.net (bundle main.b059c70b.js, wheel 115):
// opening a Segment on the public View Wheel page paints `Styled.RightColumn`
// (the Segment content panel) OVER the wheel's rim pills instead of the wheel
// making room for it -- the wheel's own SVG container never resizes on
// selection (confirmed live via a MutationObserver on its `style` attribute:
// zero mutations across the open transition), so any pill whose fixed
// on-screen position happens to fall under the panel's region gets visually
// covered. 1/8 pills covered at 1440px, 2/8 at 1280px (both widths tested
// live). This violates the HARD design law that rim pills never hide.
//
// Independent of WHY the container fails to resize (a deeper, un-root-caused
// render/memo question -- see PR description), this is a self-contained,
// always-safe correction: given a pill's OWN real screen box (after every
// other placement step has run) and the open panel's real screen box, push
// the pill left by exactly its horizontal overlap so its right edge lands
// flush with the panel's left edge. A pill that doesn't overlap the panel (or
// when no panel is open) is returned unchanged.
export function nudgeTxOutOfPanel(tx, pillBox, panelBox) {
  if (typeof tx !== "number" || !Number.isFinite(tx)) return tx;
  if (!panelBox) return tx;
  const overlapX = Math.min(pillBox.right, panelBox.right) - Math.max(pillBox.left, panelBox.left);
  const overlapY = Math.min(pillBox.bottom, panelBox.bottom) - Math.max(pillBox.top, panelBox.top);
  if (overlapX <= 0 || overlapY <= 0) return tx; // no real intersection
  const nudge = pillBox.right - panelBox.left;
  return tx - nudge;
}
